// src/lib/topology.ts — RAPORT TOPOLOGIC per strat (4.5(a), decizia lui Radu): tipurile raportului +
// clasificarea constatărilor. PUR (fără DB, fără DOM) — importabil și din *.client.ts (panoul din
// admin, W6). SQL-ul PostGIS care produce constatările e în conformity.ts (`topology_report`).
//
// Constatări:
//   overlap   — două poligoane ale ACELUIAȘI strat cu interioarele suprapuse (ST_Intersects și nu
//               ST_Touches, pe geometrii reparate cu ST_MakeValid), cu aria GEODEZICĂ a suprapunerii;
//               include conținerea (o parcelă în alta) — ST_Overlaps singur ar fi ratat-o.
//   gap       — gol între stratul unit și limita administrativă a hărții (PlanSpatial / „Limita UAT")
//   invalid   — geometrie nevalidă (ST_IsValidDetail: motiv + locație)
//   duplicate — două obiecte cu geometrii identice (ST_Equals)
// Severitate: invalid/duplicate/overlap = eroare; gap = avertisment (un gol față de limita UAT poate
// fi legitim — ape, terenuri neîncadrate). Suprapunerile/golurile de sub SLIVER_AREA_M2 sunt
// „sliver" (avertisment): datele CAD reale au micro-suprapuneri din rotunjiri pe mai toate
// muchiile comune; cele sub MIN_AREA_M2 sunt zgomot numeric și nu se raportează deloc.
// Poligonalitate: suprapunerile/golurile rulează când `config.geometry_type` e din familia poligon
// SAU — pe tipuri nedeclarate/mixte („Geometry", „Unknown") — când TOATE geometriile vii ale
// stratului au dimensiunea 2 (`bool_and(ST_Dimension(geom) = 2)`, verificat live în conformity.ts).

export const TOPOLOGY_MAX_FINDINGS = 500
/** Sub această arie o suprapunere/un gol e zgomot numeric — nu se raportează. */
export const MIN_AREA_M2 = 0.01
/** Sub această arie o suprapunere/un gol e „sliver" (avertisment), nu eroare. */
export const SLIVER_AREA_M2 = 1

export const TOPOLOGY_KINDS = ["invalid", "duplicate", "overlap", "gap"] as const
export type TopologyKind = (typeof TOPOLOGY_KINDS)[number]
export type TopologySeverity = "eroare" | "avertisment"

export interface TopologyFinding {
	kind: TopologyKind
	severity: TopologySeverity
	/** Obiectele implicate (deep-link `#/map?feature=<id>`): 1 la invalid, 2 la overlap/duplicate, 0 la gap. */
	feature_ids: string[]
	/** Aria geodezică (m²) a suprapunerii / golului. */
	area_m2?: number
	/** Motivul (ST_IsValidReason) — doar la invalid. */
	reason?: string
	/** Un punct reprezentativ [lng, lat] pentru zoom (locația defectului / ST_PointOnSurface). */
	location?: [number, number]
	/** Bbox-ul [minx, miny, maxx, maxy] al zonei (suprapunere/gol), pentru încadrare. */
	bbox?: [number, number, number, number]
}

export interface TopologySummary {
	invalid: number
	duplicate: number
	overlap: number
	gap: number
	total: number
}

export interface TopologyReport {
	map_slug: string
	layer_slug: string
	geometry_type: string
	features: number // obiecte vii cu geometrie
	/** Slug-ul limitei administrative folosite pentru goluri; "" = fără (golurile nu se evaluează). */
	boundary_layer: string
	/** Analizele care NU s-au rulat (ex. strat de puncte → fără suprapuneri/goluri; fără limită → fără goluri; timeout). */
	skipped: Array<{ kind: TopologyKind; reason: string }>
	/** TOTALURILE reale (peste plafon). */
	summary: TopologySummary
	/** Cel mult TOPOLOGY_MAX_FINDINGS, ordonate: invalid → duplicate → overlap (arie desc) → gap (arie desc). */
	findings: TopologyFinding[]
	truncated: boolean
	/**
	 * Fără ERORI (invalid/duplicate/overlap ne-sliver); golurile și sliver-ele nu strică `clean`.
	 * (Numele NU e `ok` — ar fi fost confundat cu `ok`-ul anvelopei API `{ok, data}`.) Analizele din
	 * `skipped` nu contează — un raport `clean` cu `skipped` ne-gol e „curat pe ce s-a analizat".
	 */
	clean: boolean
}

export const NIL_TOPOLOGY: TopologyReport = Object.freeze({
	map_slug: "",
	layer_slug: "",
	geometry_type: "",
	features: 0,
	boundary_layer: "",
	skipped: [],
	summary: { invalid: 0, duplicate: 0, overlap: 0, gap: 0, total: 0 },
	findings: [],
	truncated: false,
	clean: false,
})

export const TOPOLOGY_LABELS_RO: Record<TopologyKind, string> = {
	invalid: "Geometrie nevalidă",
	duplicate: "Duplicat",
	overlap: "Suprapunere",
	gap: "Gol față de limita administrativă",
}

/** Severitatea unei constatări: aria decide „sliver" la overlap/gap; gap-ul e mereu avertisment. */
export function severity_of(kind: TopologyKind, area_m2?: number): TopologySeverity {
	if (kind === "gap") {
		return "avertisment"
	}
	if (kind === "overlap") {
		return (area_m2 ?? 0) < SLIVER_AREA_M2 ? "avertisment" : "eroare"
	}
	return "eroare"
}

/** Familia geometriei stratului (din `config.geometry_type`) e poligonală? (suprapuneri/goluri au sens doar aici) */
export function is_polygon_family(geometry_type: string): boolean {
	return (geometry_type ?? "").toLowerCase().includes("polygon")
}

/**
 * Stratul e limita administrativă a planului? Aceeași regulă ca ancora extent-ului din maps.ts
 * (PlanSpatial / „Limita UAT" / „unitate administrativ") + maparea explicită pe clasa 904 PlanSpatial.
 */
export function is_boundary_layer(name: string, ordin904_class?: string | null): boolean {
	if (ordin904_class === "PlanSpatial") {
		return true
	}
	return /(planspatial|limit[aă]_?\s*(uat|admin)|unitate administrativ)/i.test(name ?? "")
}

const KIND_ORDER: Record<TopologyKind, number> = { invalid: 0, duplicate: 1, overlap: 2, gap: 3 }

/** Ordinea de afișare: felul (invalid → duplicate → overlap → gap), apoi aria descrescător. */
export function compare_findings(a: TopologyFinding, b: TopologyFinding): number {
	const k = KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
	if (k !== 0) {
		return k
	}
	return (b.area_m2 ?? 0) - (a.area_m2 ?? 0)
}

/** Ordonează și PLAFONEAZĂ constatările (totalurile rămân în `summary`). */
export function cap_findings(
	all: readonly TopologyFinding[],
	max: number = TOPOLOGY_MAX_FINDINGS,
): { findings: TopologyFinding[]; truncated: boolean } {
	const sorted = [...all].sort(compare_findings)
	return { findings: sorted.slice(0, max), truncated: sorted.length > max }
}

/** Totalurile per fel + total (din numărătorile reale, nu din lista plafonată). */
export function summarize(counts: Partial<Record<TopologyKind, number>>): TopologySummary {
	const invalid = counts.invalid ?? 0
	const duplicate = counts.duplicate ?? 0
	const overlap = counts.overlap ?? 0
	const gap = counts.gap ?? 0
	return { invalid, duplicate, overlap, gap, total: invalid + duplicate + overlap + gap }
}

/** Raportul e „curat" (`clean`) dacă nu există nicio constatare cu severitatea `eroare`. */
export function report_clean(
	findings: readonly TopologyFinding[],
	summary: TopologySummary,
): boolean {
	// summary poartă totalurile reale: erori sigure = invalid + duplicate (mereu eroare); overlap-urile
	// pot fi sliver-e (avertisment) → decid pe constatările listate; peste plafon, considerăm ne-curat.
	if (summary.invalid > 0 || summary.duplicate > 0) {
		return false
	}
	if (
		summary.overlap > 0 &&
		findings.filter((f) => f.kind === "overlap").length < summary.overlap
	) {
		return false
	}
	return !findings.some((f) => f.severity === "eroare")
}
